import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeIt from '@angular/common/locales/it';
import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { CustomDateAdapter } from './core/dates/custom-date-adapter';
import { apiNetworkErrorInterceptor } from './core/interceptors/api-network.interceptor';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { cultureInterceptor } from './core/transloco/transloco.interceptor';

//Workaround imports to allow moment date adapter to work properly
//Without these moment imports, the dates get formatted without respecting
//the selected language locale
//https://github.com/angular/components/issues/28263
import 'moment/locale/de.js';
import 'moment/locale/en-gb.js';
import 'moment/locale/it.js';
import { AppConfigService } from './core/configuration/configuration.service';
import { BrandInterceptor } from './core/interceptors/brand.interceptor';
import { StorageService } from './core/storage/storage.service';

registerLocaleData(localeIt);
registerLocaleData(localeEn);
registerLocaleData(localeDe);

/**
 * The application should use the list of supported languages obtained by the configuration API.
 * The obtained data is a list of code languages, which still need to be mapped to objects to have their label.
 * This array is used to initialize tranloco during setup, but then also as a base to provide supported languages from the api.
 */
const APP_AVAILABLE_LANGS_FALLBACK = [
    {
        id: 'en',
        label: 'English',
    },
    {
        id: 'it',
        label: 'Italiano',
    },
    {
        id: 'de',
        label: 'Deutsch',
    },
];

const APP_DEFAULT_LANG_FALLBACK = 'en';

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(BrowserModule),
        provideAppInitializer(async () => {
            try {
                const configService = inject(AppConfigService);
                const translocoService = inject(TranslocoService);
                const storageService = inject(StorageService);

                const config = await configService.loadConfig();
                // 0. Calculate true supported languages (languages that are both supported by API and frontend)
                const finalAvailableLangs = APP_AVAILABLE_LANGS_FALLBACK.filter((item) =>
                    config.languages.languages.includes(item.id)
                );

                // If there are no supported languages, throw an error
                if (finalAvailableLangs.length == 0) {
                    console.error(
                        'No supported languages found. Please check the enabled languages in StockStore Cloud settings.'
                    );
                    return;
                }

                // 1. Get stored language choice
                let activeLang = storageService.getData('language');

                // 2. If not stored language, try to obtain it from browser
                if (!activeLang) {
                    const browserLang = navigator.languages?.length ? navigator.languages[0] : navigator.language;

                    // Normalize browserLang (eg. "it-IT" -> "it") and check if it's supported, then set active lang directly, we will check later if it's supported
                    activeLang = browserLang.toLowerCase().split('-')[0];
                }

                // 3. Check if the current active lang is supported and use a fallback if it's not
                const isSupported = finalAvailableLangs.some((lang) => lang.id == activeLang);
                if (!isSupported) {
                    // Check if the default fallback is supported, otherwise use the first available language
                    const isFallbackSupported = finalAvailableLangs.some(
                        (lang) => lang.id == APP_DEFAULT_LANG_FALLBACK
                    );
                    activeLang = isFallbackSupported ? APP_DEFAULT_LANG_FALLBACK : finalAvailableLangs[0]?.id;
                }

                // Set as available languages the languages that are enabled both by the API and frontend.
                translocoService.setAvailableLangs(finalAvailableLangs);
                translocoService.setActiveLang(activeLang);
            } catch (error) {
                console.error('Error during app initialization', error);
            }
        }),
        provideHttpClient(withInterceptors([cultureInterceptor, apiNetworkErrorInterceptor]), withInterceptorsFromDi()),
        provideAnimations(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BrandInterceptor,
            multi: true,
        },
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'LL',
                },
                display: {
                    dateInput: 'LL',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            },
        },
        // Custom Date Adapter
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
        },

        // Transloco Config
        provideTransloco(
            /**
             * Note: language related configs are just some basic values to init Transloco.
             * These values, such as the available langs, and the active lang, will be updated on runtime
             * when the config is loaded.
             */
            {
                config: {
                    availableLangs: APP_AVAILABLE_LANGS_FALLBACK,
                    defaultLang: localStorage.getItem('language') || APP_DEFAULT_LANG_FALLBACK,
                    fallbackLang: APP_DEFAULT_LANG_FALLBACK,
                    reRenderOnLangChange: true,
                    prodMode: !isDevMode(),
                },
                loader: TranslocoHttpLoader,
            }
        ),
        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-tb',
                themes: [
                    {
                        id: 'theme-tb',
                        name: 'Default',
                    },
                ],
            },
        }),
    ],
};
